import * as React from "react";
import { Table } from "../../../newcomponents/table/table";
import { useTableKindId } from "../../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../../newcomponents/table/data/useTableItemQueryData";
import { TableHeaderRefreshButton } from "../../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionNoLinkColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { AgentLastActiveTextColumn } from "../../../newcomponents/table/columns/agent/lastActiveText";
import { TagsColumn } from "../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { AgentTableItem as AgentTableItemBase, agentHelpers } from "../../../mst/kinds/agent.helpers";
import { linksOf } from "../../../services/cpln";
import { Agent } from "../../../schema/types/agent";
import { NGButton } from "../../../newcomponents/button/Button";
import { Modal } from "antd";
import { Base } from "../../../schema/types/base";
import { useAgentInfosReducer } from "../../../components/detail/agentInfosReducer";
import { AGENT_UPDATE_INTERVAL_MS } from "../../../envVariables";

interface AgentTableItem extends AgentTableItemBase {
  selfLink: string;
}

interface TargetTableItem extends Base {
  selfLink: string;
}

interface Props {
  title: string;
  selectionsToRemove: string[];
  onSelectionsToRemoveChange: (values: string[], items?: TargetTableItem[]) => void;
  selectionsToAdd: string[];
  onSelectionsToAddChange: (values: string[], items?: TargetTableItem[]) => void;
  targets: TargetTableItem[];
  onRemove: () => void;
  onOkAddModal: () => void;
}
export const AgentTable: React.FC<Props> = ({
  title, //
  selectionsToRemove,
  onSelectionsToRemoveChange,
  selectionsToAdd,
  onSelectionsToAddChange,
  targets,
  onRemove,
  onOkAddModal,
}) => {
  const { state, fetchAgentInfos } = useAgentInfosReducer();

  const [isAddingItem, setIsAddingItem] = React.useState(false);

  const { kind, id } = useTableKindId("agent", "policy-detail-target-list-agent");
  const qData = useTableItemQueryData<Agent>(kind, { fetchAllPages: true });

  React.useEffect(() => {
    const unsubscribe = fetchAgentInfos(qData.visibleItems.map((i) => i.name), AGENT_UPDATE_INTERVAL_MS);
    return () => {
      unsubscribe();
    };
  }, [qData.visibleItems.length]);

  const targetLinks = targets.map((t) => t.selfLink);
  const agentTableItems: AgentTableItem[] = qData.visibleItems //
    .map((agent) => {
      const agentInfos = state.agentInfoMap[agent.name];
      const isActive = agentHelpers.getIsActive(agent, agentInfos);
      const lastActiveText = agentHelpers.getLastActiveText(agent, agentInfos);
      const selfLink = linksOf(agent).self!;

      return {
        ...agent,
        isActive: isActive,
        lastActiveText: lastActiveText,
        selfLink: selfLink,
      };
    })
    .filter((identity) => {
      return !targetLinks.includes(identity.selfLink);
    });

  return (
    <>
      <Table<TargetTableItem>
        title={title}
        data={targets}
        tableId={`${id}-targets`}
        selectMode="multi"
        selectKey={"selfLink"}
        selections={selectionsToRemove}
        onSelectionsChange={onSelectionsToRemoveChange}
        headerRenderer={() => {
          return (
            <>
              <NGButton
                variant={"danger"}
                size={"small"}
                outlined
                disabled={selectionsToRemove.length < 1}
                onClick={onRemove}
              >
                Remove
              </NGButton>
              <NGButton variant={"action"} size={"small"} onClick={() => setIsAddingItem(true)}>
                Add
              </NGButton>
            </>
          );
        }}
        columns={[
          NameDescriptionNoLinkColumn(),
          // AgentLastActiveTextColumn(),
          TagsColumn(),
          CreatedColumn(),
          LastModifiedColumn(),
        ]}
      />
      {isAddingItem ? (
        <Modal
          width={"90vw"}
          styles={{ body: { height: "70vh", padding: 0 } }}
          open={isAddingItem}
          onCancel={() => setIsAddingItem(false)}
          footer={
            <div className="modal-actions">
              <NGButton variant="secondary" onClick={() => setIsAddingItem(false)}>
                Cancel
              </NGButton>
              <NGButton
                variant="primary"
                onClick={() => {
                  onOkAddModal();
                  setIsAddingItem(false);
                }}
              >
                OK
              </NGButton>
            </div>
          }
          destroyOnClose={true}
          closable={false}
          maskClosable={false}
        >
          <div className="h-full overflow-auto p-1">
            <Table<AgentTableItem>
              title={title}
              data={agentTableItems}
              tableId={id}
              selectMode="multi"
              selectKey={"selfLink"}
              selections={selectionsToAdd}
              onSelectionsChange={onSelectionsToAddChange}
              headerRenderer={() => {
                return (
                  <>
                    <TableHeaderRefreshButton onClick={qData.fetchItems} />
                    <CustomTableCPLNQuery
                      tableId={id}
                      kind={kind}
                      onQuery={qData.setQuery}
                      isLoading={qData.isLoadingQueried}
                    />
                  </>
                );
              }}
              enableFilter
              isLoading={qData.isLoading}
              columns={[
                NameDescriptionNoLinkColumn(),
                AgentLastActiveTextColumn(),
                TagsColumn(),
                CreatedColumn(),
                LastModifiedColumn(),
              ]}
            />
          </div>
        </Modal>
      ) : null}
    </>
  );
};
